import React, { useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import AmenityForm from "./components/AmenityForm";
import { AmenityFormData } from "./utils/amenitySchema";
import {
  useAmenity,
  useUpdateAmenity,
} from "@/components/pages/amenities/hooks/amenities.hooks";
import { fileService } from "@/services/ServiceRegistry";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { hasFrontdeskBlockPermission } from "@/utils/accessControl";
import {
  getAmenityAccess,
  getAmenitySaveErrorKey,
} from "./utils/amenityAccess";

const EditAmenityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: amenity, isLoading } = useAmenity(id || "");
  const { mutateAsync, isPending } = useUpdateAmenity();
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const { canEdit } = getAmenityAccess(
    hasFrontdeskBlockPermission(
      userProfile,
      "frontdesk_hotel_settings",
      "administrate",
    ),
  );

  const initialValues: Partial<AmenityFormData> | undefined = useMemo(() => {
    if (!amenity) return undefined;
    return {
      name: amenity.name,
      description: amenity.description,
      category: amenity.category,
      isActive: amenity.isActive,
      photoId: amenity.photoId ?? undefined,
      serviceIds: amenity.services?.map((service) => service.id) ?? [],
      schedules: amenity.schedules?.map((s) => ({
        days: s.days,
        startTime: s.startTime,
        endTime: s.endTime,
      })) || [{ days: [], startTime: "", endTime: "" }],
    };
  }, [amenity]);

  const handleSubmit = async (
    data: AmenityFormData,
    selectedImage: File | null,
  ) => {
    if (!id) return;
    try {
      let photoId: string | undefined = amenity?.photoId ?? undefined;
      if (selectedImage) {
        try {
          photoId = await fileService.uploadFileWithPresignedUrl(selectedImage);
        } catch (e) {
          console.error("Failed to upload amenity photo:", e);
        }
      }

      await mutateAsync({
        id,
        data: {
          name: data.name,
          description: data.description,
          category: data.category,
          isActive: data.isActive,
          photoId,
          serviceIds: data.serviceIds,
          schedules: data.schedules.map((s) => ({
            days: s.days,
            startTime: s.startTime,
            endTime: s.endTime,
          })),
        },
      });

      navigate("/amenities");
    } catch (err) {
      console.error("Failed to update amenity:", err);
      toast.error(t(getAmenitySaveErrorKey(err, "update")));
    }
  };

  if (!canEdit) return <Navigate to="/amenities" replace />;

  if (isLoading || !initialValues) {
    return <div className="p-12">{t("amenities.loading.loading")}</div>;
  }

  return (
    <div className="mx-auto max-w-[960px] p-12">
      <AmenityForm
        titleText={t("amenities.form.editTitle")}
        onSubmit={handleSubmit}
        isSubmitting={isPending}
        submitButtonText={t("amenities.form.save")}
        initialValues={initialValues}
      />
    </div>
  );
};

export default EditAmenityPage;
